import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Users, Clock } from "lucide-react";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Games() {
  const [games, setGames] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locFilter, setLocFilter] = useState("all");

  const load = (loc) => {
    const params = loc && loc !== "all" ? { location_id: loc } : {};
    api.get("/games", { params }).then((r) => setGames(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    load("all");
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-5 py-12">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight">Our Games</h1>
          <p className="text-slate-400 mt-2">Filter by location, or pick a game to see where the truck is parked.</p>
        </div>
        <div className="w-full sm:w-64">
          <Select value={locFilter} onValueChange={(v) => { setLocFilter(v); load(v); }}>
            <SelectTrigger data-testid="location-filter" className="bg-[#131a29] border-white/10">
              <SelectValue placeholder="All locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {games.map((g) => (
          <Link key={g.id} to={`/book/${g.id}`} data-testid={`game-${g.id}`}
            className="block rounded-2xl overflow-hidden border border-white/10 bg-[#131a29] card-hover">
            <div className="relative h-52">
              {g.image_url ? <img src={g.image_url} alt={g.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-violet-900/40 to-slate-800" />}
              <div className="absolute inset-0 bg-gradient-to-t from-[#131a29] to-transparent" />
              <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-mono border border-white/20 bg-black/40 backdrop-blur-md">{g.difficulty}</span>
            </div>
            <div className="p-5">
              <h3 className="font-display font-bold text-xl">{g.name}</h3>
              <p className="text-xs font-mono uppercase tracking-wider text-violet-400 mt-1">{g.theme}</p>
              <p className="text-sm text-slate-400 mt-3 min-h-[40px] line-clamp-2">{g.description}</p>
              <div className="mt-4 flex items-center gap-4 text-xs text-slate-400">
                <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {g.min_players}-{g.max_players}</span>
                <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {g.duration_min}m</span>
                <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {g.location_count || 0}</span>
              </div>
              <div className="mt-4 inline-flex items-center gap-1 text-violet-300 font-semibold text-sm">Book now <ArrowRight className="h-4 w-4" /></div>
            </div>
          </Link>
        ))}
        {games.length === 0 && <p className="text-slate-400">No games available at this location yet.</p>}
      </div>
    </div>
  );
}
